import { type SetStateAction, useState, type Dispatch, useEffect, useCallback } from 'react';

type AcceptableType =
    | number
    | string
    | null
    | undefined
    | boolean
    | AcceptableObject
    | AcceptableType[]
    | AcceptableObject[];

type AcceptableObject = {
    [key: string]: AcceptableType;
};

type CanParse = AcceptableType | AcceptableObject;

type UseLocalStorage<Data extends CanParse> = {
    key: string;
    value: Data;
};

export const useLocalStorage = <Data extends CanParse>({ key, value }: UseLocalStorage<Data>) => {
    const [data, setData] = useState<Data>(() => {
        const retrieved = localStorage.getItem(key);

        return retrieved ? JSON.parse(retrieved) : value;
    });

    const clear = useCallback(() => {
        setData(value);
    }, [value]);

    useEffect(() => {
        localStorage.setItem(key, JSON.stringify(data));
    }, [data]);

    return [data, setData, clear] as [Data, Dispatch<SetStateAction<Data>>, () => void];
};
